import React from 'react';
import Card from './card';
import ProgressBar from './progressBar';

const COLORS = ['#FFD600', '#FFB347', '#FF99AA'];

const STYLE_ORDER_MAP = {
  // Sparkling
  "Fruit Driven Sparkling": 1,
  "Complex Sparkling": 2,
  // White
  "Fresh, Unoaked White": 1,
  "Earthy White": 2,
  "Aromatic White": 3,
  "Rich, Oaky White": 4,
  // Rosé
  "Blush Rosé": 1,
  "Dry Rosé": 2,
  // Red
  "Mild, Mannered Red": 1,
  "Soft, Fruity Red": 2,
  "Fresh, Spicy Red": 3,
  "Powerful Red": 4,
  // Orange
  "Aromatic Orange": 1,
  "Fruity Orange": 2,
  "Rich Orange": 3,
  // Dessert
  "Sparkling Dessert": 1,
  "Subtle, Aromatic Dessert": 2,
  "Rich, Fruity Dessert": 3,
};

const sortStyles = (styles) => {
  if (!styles) return [];
  return [...styles].sort((a, b) => {
    const orderA = STYLE_ORDER_MAP[a.name] || 999;
    const orderB = STYLE_ORDER_MAP[b.name] || 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });
};

const WineDashboardStats = ({
  total,
  newWines,
  removed,
  lastUpdated,
  wineCategory,
  styleDistribution,
  topVarietals,
  regionalDistribution,
  dietaryConsiderations,
  recentUpdates,
  wineActivityData = [],
  onScroll,
  wineActivityTotalCount = 0,
  wineActivityLoading = false,
  userRole
}) => (
  <div className="space-y-6 text-left mt-6">
    {/* Summary Stats Row */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Wines */}
      <Card className="p-1 flex flex-col justify-between h-28 relative overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
        <div className="flex justify-between items-start">
          <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wider">Total Wines</span>
          <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
            <path d="M11 20V9.82C8.63 9.4 6.64 8 5.62 6.01 4.5 3.82 5.09 1.49 5.25 1h13.5c.16.49.75 2.82-.37 5.01-1.02 1.99-3.01 3.39-5.38 3.81V20h3v2H8v-2h3zM7.18 3c-.15 1.05-.2 2.37.58 3.9.72 1.41 2.22 2.15 4.24 2.15s3.52-.74 4.24-2.15c.78-1.53.73-2.85.58-3.9H7.18z" />
          </svg>
        </div>
        <span className="text-[32px] font-black text-gray-900 tracking-tight">{total}</span>
      </Card>

      {/* New Wines */}
      <Card className="p-1 flex flex-col justify-between h-28 relative overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
        <div className="flex justify-between items-start">
          <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wider">New Wines (30 Days)</span>
          <div className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center text-white">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
          </div>
        </div>
        <span className="text-[32px] font-black text-gray-900 tracking-tight">{newWines}</span>
      </Card>

      {/* Removed */}
      <Card className="p-1 flex flex-col justify-between h-28 relative overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
        <div className="flex justify-between items-start">
          <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wider">Removed (30 Days)</span>
          <div className="w-5 h-5 bg-red-500 rounded flex items-center justify-center text-white">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>
          </div>
        </div>
        <span className="text-[32px] font-black text-red-600 tracking-tight">{removed}</span>
      </Card>

      {/* Last Updated */}
      <Card className="p-1 flex flex-col justify-between h-28 relative overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
        <div className="flex justify-between items-start">
          <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wider">Last Updated</span>
          <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
        </div>
        <span className="text-xl font-black text-gray-900 mt-1">{lastUpdated}</span>
      </Card>
    </div>

    {/* Detailed Charts Grid */}
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 mb-6">
      {/* Wine Category Distribution */}
      <Card className="p-6 h-full lg:col-span-2 bg-white border border-gray-100 shadow-sm rounded-2xl">
        <div className="mb-6">
          <h3 className="text-[16px] font-bold text-gray-900">Wine Category Distribution</h3>
          <p className="text-[12px] text-gray-500 mt-1">Percentage breakdown of all wines by category.</p>
        </div>
        <div className="space-y-5">
          {wineCategory.map((item) => {
            const color =
              item.label.toLowerCase().includes('red') ? '#9F1239' :
                item.label.toLowerCase().includes('white') ? '#FCD34D' :
                  item.label.toLowerCase().includes('rosé') || item.label.toLowerCase().includes('rose') ? '#F472B6' :
                    item.label.toLowerCase().includes('sparkling') ? '#FACC15' :
                      item.label.toLowerCase().includes('orange') ? '#FB923C' :
                        '#D97706';
            const count = Math.round((total * item.value) / 100);

            return (
              <div key={item.label} className="w-full">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }}></span>
                    <span className="text-[13px] font-medium text-gray-700">{item.label}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[12px] text-gray-400 font-medium">{count} wines</span>
                    <span className="text-[13px] font-bold text-gray-900">{item.value}%</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${item.value}%`, backgroundColor: color }}></div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      {/* Regional Distribution */}
      <Card className="p-6 h-full lg:col-span-1 bg-white border border-gray-100 shadow-sm rounded-2xl">
        <div className="mb-6">
          <h3 className="text-[16px] font-bold text-gray-900">Regional Distribution</h3>
          <p className="text-[12px] text-gray-500 mt-1">Shows the percentage of wines by region.</p>
        </div>
        <div className="space-y-5">
          {regionalDistribution.map((d) => (
            <div key={d.label} className="w-full">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[13px] font-medium text-gray-700">{d.label}</span>
                <span className="text-[12px] text-gray-500">{d.value}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div className="h-full rounded-full bg-[#9F1239]" style={{ width: `${d.value}%` }}></div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>

    {/* Style Coverage and Characteristics Row */}
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 mb-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Wine Style Coverage */}
        <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
          <div className="mb-8">
            <h3 className="text-[16px] font-bold text-gray-900">Wine Style Coverage</h3>
            <p className="text-[12px] text-gray-500 mt-1">Top styles across your wine collection.</p>
          </div>

          <div className="space-y-8">
            {/* If styleDistribution is an array (legacy), fallback. Else render grouped layout */}
            {Array.isArray(styleDistribution) ? (
              (styleDistribution || []).map((style) => {
                const color = style.label.toLowerCase().includes('red') ? '#9F1239' : style.label.toLowerCase().includes('white') ? '#FBbf24' : '#9F1239';
                const pct = style.value || 0;
                const count = style.count || Math.round((total * (pct || 0)) / 100);
                return (
                  <div key={style.label} className="space-y-1.5">
                    <div className="flex justify-between items-end">
                      <span className="text-[13px] text-gray-600 font-medium">{style.label}</span>
                      <span className="text-[12px] font-bold text-gray-900">{count} wines <span className="text-gray-400 font-normal">({pct}%)</span></span>
                    </div>
                    <div className="w-full bg-gray-50 h-1 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }}></div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col gap-10">
                {/* 1. Sparkling & 2. White */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                  {/* Sparkling */}
                  <div className="space-y-4">
                    <div className="border-l-4 border-[#FACC15] pl-3 py-0.5 mb-4">
                      <h4 className="text-[14px] font-black uppercase text-gray-900 tracking-wider">Sparkling</h4>
                    </div>
                    {sortStyles(styleDistribution['Sparkling'] || []).map(style => (
                      <div key={style.name} className="space-y-1.5">
                        <div className="flex justify-between items-baseline">
                          <span className={`${style.count > 0 ? 'text-gray-700 font-bold' : 'text-gray-400 font-medium'} text-[12px]`}>{style.name}</span>
                          <span className="text-[12px] font-bold text-gray-900">{style.count} <span className="text-gray-400 font-medium text-[10px]">({style.percentage}%)</span></span>
                        </div>
                        <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${style.count > 0 ? 'bg-[#FACC15]' : 'bg-gray-200'}`} style={{ width: `${style.percentage}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* White Wine */}
                  <div className="space-y-4">
                    <div className="border-l-4 border-[#FCD34D] pl-3 py-0.5 mb-4">
                      <h4 className="text-[14px] font-black uppercase text-gray-900 tracking-wider">White Wine</h4>
                    </div>
                    {sortStyles(styleDistribution['White'] || []).map(style => (
                      <div key={style.name} className="space-y-1.5">
                        <div className="flex justify-between items-baseline">
                          <span className={`${style.count > 0 ? 'text-gray-700 font-bold' : 'text-gray-400 font-medium'} text-[12px]`}>{style.name}</span>
                          <span className="text-[12px] font-bold text-gray-900">{style.count} wines <span className="text-gray-400 font-medium">({style.percentage}%)</span></span>
                        </div>
                        <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${style.count > 0 ? 'bg-[#FCD34D]' : 'bg-gray-200'}`} style={{ width: `${style.percentage}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 3. Rosé & 4. Red */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 border-t border-gray-50 pt-8">
                  {/* Rosé Wine */}
                  <div className="space-y-4">
                    <div className="border-l-4 border-[#F472B6] pl-3 py-0.5 mb-4">
                      <h4 className="text-[14px] font-black uppercase text-gray-900 tracking-wider">Rosé Wine</h4>
                    </div>
                    {sortStyles(styleDistribution['Rosé'] || styleDistribution['Rose'] || []).map(style => (
                      <div key={style.name} className="space-y-1.5">
                        <div className="flex justify-between items-baseline">
                          <span className={`${style.count > 0 ? 'text-gray-700 font-bold' : 'text-gray-400 font-medium'} text-[12px]`}>{style.name}</span>
                          <span className="text-[12px] font-bold text-gray-900">{style.count} <span className="text-gray-400 font-medium text-[10px]">({style.percentage}%)</span></span>
                        </div>
                        <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${style.count > 0 ? 'bg-[#F472B6]' : 'bg-gray-200'}`} style={{ width: `${style.percentage}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Red Wine */}
                  <div className="space-y-4">
                    <div className="border-l-4 border-[#9F1239] pl-3 py-0.5 mb-4">
                      <h4 className="text-[14px] font-black uppercase text-gray-900 tracking-wider">Red Wine</h4>
                    </div>
                    {sortStyles(styleDistribution['Red'] || []).map(style => (
                      <div key={style.name} className="space-y-1.5">
                        <div className="flex justify-between items-baseline">
                          <span className={`${style.count > 0 ? 'text-gray-700 font-bold' : 'text-gray-400 font-medium'} text-[12px]`}>{style.name}</span>
                          <span className="text-[12px] font-bold text-gray-900">{style.count} wines <span className="text-gray-400 font-medium">({style.percentage}%)</span></span>
                        </div>
                        <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${style.count > 0 ? 'bg-[#9F1239]' : 'bg-gray-200'}`} style={{ width: `${style.percentage}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 5. Orange & 6. Dessert */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 border-t border-gray-50 pt-8">
                  {/* Orange Wine */}
                  <div className="space-y-4">
                    <div className="border-l-4 border-[#FB923C] pl-3 py-0.5 mb-4">
                      <h4 className="text-[14px] font-black uppercase text-gray-900 tracking-wider">Orange Wine</h4>
                    </div>
                    {sortStyles(styleDistribution['Orange'] || []).map(style => (
                      <div key={style.name} className="space-y-1.5">
                        <div className="flex justify-between items-baseline">
                          <span className={`${style.count > 0 ? 'text-gray-700 font-bold' : 'text-gray-400 font-medium'} text-[12px]`}>{style.name}</span>
                          <span className="text-[12px] font-bold text-gray-900">{style.count} wines <span className="text-gray-400 font-medium">({style.percentage}%)</span></span>
                        </div>
                        <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${style.count > 0 ? 'bg-[#FB923C]' : 'bg-gray-200'}`} style={{ width: `${style.percentage}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Dessert Wine */}
                  <div className="space-y-4">
                    <div className="border-l-4 border-[#D97706] pl-3 py-0.5 mb-4">
                      <h4 className="text-[14px] font-black uppercase text-gray-900 tracking-wider">Dessert Wine</h4>
                    </div>
                    {sortStyles(styleDistribution['Dessert'] || []).map(style => (
                      <div key={style.name} className="space-y-1.5">
                        <div className="flex justify-between items-baseline">
                          <span className={`${style.count > 0 ? 'text-gray-700 font-bold' : 'text-gray-400 font-medium'} text-[12px]`}>{style.name}</span>
                          <span className="text-[12px] font-bold text-gray-900">{style.count} wines <span className="text-gray-400 font-medium">({style.percentage}%)</span></span>
                        </div>
                        <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${style.count > 0 ? 'bg-[#D97706]' : 'bg-gray-200'}`} style={{ width: `${style.percentage}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Most Common Grape Varietals */}
        <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
          <div className="mb-6">
            <h3 className="text-[16px] font-bold text-gray-900">Most Common Grape Varietals</h3>
            <p className="text-[12px] text-gray-500 mt-1">Top grape varietals across your wine collection.</p>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide">
            {(topVarietals || []).map((v) => {
              const pct = v.value || 0;
              const count = v.count || Math.round((total * (pct || 0)) / 100);
              return (
                <div key={v.label} className="flex-none w-[120px] p-4 rounded-xl bg-gray-50 flex flex-col items-center justify-center text-center">
                  <span className="text-2xl font-black text-[#9F1239] mb-1">{count}</span>
                  <span className="text-[11px] font-bold text-gray-900 leading-tight">{v.label}</span>
                  <span className="text-[10px] text-gray-500 mt-1">{pct}% of wines</span>
                </div>
              );
            })}
            {(topVarietals || []).length === 0 && (
              <div className="p-4 rounded-xl bg-gray-50 col-span-full text-center">No varietal data available</div>
            )}
          </div>
        </Card>
      </div>

      {/* Right Column Stack */}
      <div className="lg:col-span-1 space-y-4 flex flex-col h-full">
        {/* Wine Characteristics & Composition */}
        <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
          <div className="mb-6">
            <h3 className="text-[15px] font-bold text-gray-900">Wine Characteristics & Composition</h3>
            <p className="text-[11px] text-gray-500 mt-1">Breakdown of sustainability, filtration, and sweetness profile attributes across your wine list.</p>
          </div>

          <div className="bg-[#FFF7ED] rounded-xl p-3">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {(dietaryConsiderations || []).map((char, idx) => {
                const label = char.label || '';
                const raw = String(char.value || char.pct || '0%');
                const pct = raw.endsWith('%') ? raw.replace('%', '') : raw;
                const count = char.count || (total ? Math.round((Number(pct || 0) / 100) * total) : '');

                const iconMap = {
                  'Organic': '🌿',
                  'Biodynamic': '🌍',
                  'Vegan': '🥬',
                  'Unfiltered': '🌫️',
                  'Dry Wines': '💧',
                  'Residual Sugar': '🍯',
                  'Has Residual Sugar': '🍯',
                };
                const icon = iconMap[label] || '🍷';

                return (
                  <div key={idx} className="bg-white rounded-xl py-3 px-2 flex flex-col items-center text-center shadow-sm border border-orange-100/50">
                    <span className="text-xl mb-1.5">{icon}</span>
                    <p className="text-[13px] font-bold text-gray-800 leading-tight mb-0.5">{label}</p>
                    <p className="text-[11px] text-gray-500 font-medium">
                      {char.count ? `${char.count} (${pct}%)` : `${pct}%`}
                    </p>
                  </div>
                );
              })}
              {(dietaryConsiderations || []).length === 0 && (
                <div className="py-8 text-center text-gray-400 text-xs col-span-full italic">
                  No characteristics data available
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>

    {/* Recent Wine Activity Section */}
    {userRole !== 'employee' &&
      <div className="bg-white p-4 sm:p-6 lg:p-8 text-start rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-md w-full mt-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-[17px] font-black text-gray-900 tracking-tight">
            Recent Wine Activity
          </h3>
          {wineActivityTotalCount > 0 && (
            <span className="text-[12px] font-medium text-gray-400">
              {wineActivityData.length} of {wineActivityTotalCount} activities
            </span>
          )}
        </div>

        <div
          onScroll={onScroll}
          className="space-y-2 h-[850px] overflow-y-auto pr-2 scroll-smooth"
        >
          {wineActivityData.length === 0 && !wineActivityLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="text-sm font-medium">No activity yet</span>
            </div>
          ) : (
            wineActivityData.map((u, idx) => {
              const action = u.action || "update";
              const text =
                u.details?.description ||
                (action.includes("created")
                  ? "New wine added"
                  : action.includes("archived")
                    ? "Wine archived"
                    : action.includes("restored")
                      ? "Wine restored"
                      : "Wine item updated");
              const user = u.user_uuid;
              const subtitle = user
                ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                : "";
              const hours = Math.floor(
                (Date.now() - new Date(u.createdAt).getTime()) /
                (1000 * 60 * 60)
              );
              const time =
                hours < 24
                  ? `${hours} hrs ago`
                  : `${Math.floor(hours / 24)} days ago`;

              const { icon, bg } = (function () {
                if (action.includes("created"))
                  return {
                    icon: (
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    ),
                    bg: "bg-green-50",
                  };
                if (action.includes("archived"))
                  return {
                    icon: (
                      <svg
                        className="w-5 h-5 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    ),
                    bg: "bg-red-50",
                  };
                if (action.includes("restored"))
                  return {
                    icon: (
                      <svg
                        className="w-5 h-5 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    ),
                    bg: "bg-purple-50",
                  };
                return {
                  icon: (
                    <svg
                      className="w-5 h-5 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  ),
                  bg: "bg-blue-50",
                };
              })();

              return (
                <div
                  key={`${u._id || idx}-${idx}`}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border border-gray-50 hover:border-[#FDE6D2] hover:bg-[#FFF9F3]/30 transition-all group gap-3"
                >
                  <div className="flex items-center gap-4 sm:gap-5">
                    <div
                      className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform`}
                    >
                      {icon}
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[13px] sm:text-[14px] font-black text-gray-900 leading-tight truncate">
                        {text}
                      </span>
                      {subtitle && (
                        <span className="text-[12px] font-medium text-gray-500 mt-1 line-clamp-1">{`By ${subtitle}`}</span>
                      )}
                    </div>
                  </div>
                  <span className="text-[12px] font-bold text-gray-400 whitespace-nowrap self-end sm:self-center">
                    {time}
                  </span>
                </div>
              );
            })
          )}

          {/* Loading indicator */}
          {wineActivityLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="w-6 h-6 border-2 border-gray-200 border-t-[#881337] rounded-full animate-spin"></div>
              <span className="ml-3 text-sm font-medium text-gray-500">Loading more...</span>
            </div>
          )}
        </div>
      </div>}
  </div>
);

export default WineDashboardStats;